import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class OrdersService {
  apiUrl: string = environment.apiUrl;

  constructor(private httpClient: HttpClient) { }

  getAll() {
    return this.httpClient.get(`${this.apiUrl}orders`);
  }

  getMine() {
    return this.httpClient.get(`${this.apiUrl}orders/mine`);
  }

  filter(payload: any) {
    return this.httpClient.post(`${this.apiUrl}orders/filter`, payload);
  }

  filterMine(payload: any) {
    return this.httpClient.post(`${this.apiUrl}orders/filter/mine`, payload);
  }

  filterCancelledOrderItems(payload: any) {
    return this.httpClient.post(`${this.apiUrl}orders/cancel-order-items/filter`, payload);
  }

  getById(id: number) {
    return this.httpClient.get(`${this.apiUrl}orders/${id}`);
  }

  getAudit(id: number) {
    return this.httpClient.get(`${this.apiUrl}orders/${id}/audit`);
  }

  getOrderHistory(id: number) {
    return this.httpClient.get(`${this.apiUrl}orders/${id}/history`);
  }

  printReceipt(id: number, revised: boolean = false) {
    const params = new HttpParams().set('revised', String(revised));
    return this.httpClient.get(`${this.apiUrl}orders/${id}/receipt`, {
      params,
      responseType: 'blob'
    });
  }

  printItemBarcodes(id: number) {
    return this.httpClient.get(`${this.apiUrl}orders/${id}/item-barcodes`, {
      responseType: 'blob'
    });
  }

  downloadVendorFile(id: number) {
    return this.httpClient.get(`${this.apiUrl}orders/${id}/vendor-file`, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  uploadResultFile(id: number, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post(`${this.apiUrl}orders/${id}/result-file`, formData);
  }

  uploadCustomerReviewResultFile(id: number, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post(`${this.apiUrl}orders/${id}/customer-review-result-file`, formData);
  }

  downloadItemCertificates(orderIds: number[]) {
    return this.httpClient.post(`${this.apiUrl}orders/download/item-certificates`, { orderIds }, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  downloadResultFiles(orderIds: number[]) {
    return this.httpClient.post(`${this.apiUrl}orders/download/result-files`, { orderIds }, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  getOrderFileManagementRows() {
    return this.httpClient.get(`${this.apiUrl}orders/file-management`);
  }

  downloadInputFiles(orderIds: number[]) {
    return this.httpClient.post(`${this.apiUrl}orders/download/input-files`, { orderIds }, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  deleteInputFiles(orderIds: number[]) {
    return this.httpClient.post(`${this.apiUrl}orders/input-files/delete`, { orderIds });
  }

  mailItemCertificates(orderIds: number[]) {
    return this.httpClient.post(`${this.apiUrl}orders/mail/item-certificates`, { orderIds });
  }

  mailResultFiles(orderIds: number[]) {
    return this.httpClient.post(`${this.apiUrl}orders/mail/result-files`, { orderIds });
  }

  previewAccountVerification(orderIds: number[], file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const params = new HttpParams().set('orderIds', (orderIds || []).join(','));
    return this.httpClient.post(`${this.apiUrl}orders/account-verification/preview`, formData, { params });
  }

  getAccountVerificationSummary(orderIds: number[]) {
    const params = new HttpParams().set('orderIds', (orderIds || []).join(','));
    return this.httpClient.get(`${this.apiUrl}orders/account-verification/summary`, { params });
  }

  saveAccountVerification(results: any[]) {
    return this.httpClient.post(`${this.apiUrl}orders/account-verification/save`, { results });
  }

  manualAccountVerification(orderIds: number[], file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const params = new HttpParams().set('orderIds', (orderIds || []).join(','));
    return this.httpClient.post(`${this.apiUrl}orders/account-verification/manual`, formData, { params });
  }

  skipAccountVerification(id: number) {
    return this.httpClient.patch(`${this.apiUrl}orders/${id}/account-verification/skip`, null);
  }

  printReceiptBulk(orderIds: number[], revised: boolean = false) {
    const params = new HttpParams().set('revised', String(revised));
    return this.httpClient.post(`${this.apiUrl}orders/print/receipt`, { orderIds }, {
      params,
      responseType: 'blob'
    });
  }

  printOrderQr(orderIds: number[]) {
    return this.httpClient.post(`${this.apiUrl}orders/print/order-qr`, { orderIds }, {
      responseType: 'blob'
    });
  }

  printItemQr(orderIds: number[], orderItemIds: number[]) {
    return this.httpClient.post(`${this.apiUrl}orders/print/item-qr`, { orderIds, orderItemIds }, {
      responseType: 'blob'
    });
  }

  mailReceipt(orderIds: number[]) {
    return this.httpClient.post(`${this.apiUrl}orders/mail/receipt`, { orderIds });
  }

  getByOrderNo(orderNo: string) {
    return this.httpClient.get(`${this.apiUrl}orders/by-order-no`, {
      params: { orderNo }
    });
  }

  getByOrderItemNo(orderItemNo: string) {
    return this.httpClient.get(`${this.apiUrl}orders/item-no/${encodeURIComponent(orderItemNo)}`);
  }

  orderFileProcess(id: number) {
    return this.httpClient.post(`${this.apiUrl}orders/${id}/order-file-process`, null, {
      observe: 'events',
      reportProgress: true
    });
  }

  getOrderItemAudit(orderItemId: number) {
    return this.httpClient.get(`${this.apiUrl}orders/items/${orderItemId}/audit`);
  }

  updateOrderItem(orderItemId: number, payload: any) {
    return this.httpClient.put(`${this.apiUrl}orders/items/${orderItemId}`, payload);
  }

  submitCustomerReviewResponse(id: number) {
    return this.httpClient.post(`${this.apiUrl}orders/${id}/customer-review-response`, null);
  }

  uploadOrderItemCertificateFile(orderItemId: number, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post(`${this.apiUrl}orders/items/${orderItemId}/certificate-file`, formData);
  }

  generateOtp(payload: any) {
    return this.httpClient.post(`${this.apiUrl}orders/otp`, payload);
  }

  validateOtp(payload: any) {
    return this.httpClient.post(`${this.apiUrl}orders/otp/validate`, payload);
  }

  skipOtp(payload: any) {
    return this.httpClient.post(`${this.apiUrl}orders/otp/skip`, payload);
  }

  validateCancelDeliveryOtp(payload: { orderNo: string; otp: string; itemIds: number[] }) {
    return this.httpClient.post(`${this.apiUrl}orders/cancel-delivery/otp/validate`, payload);
  }

  skipCancelDeliveryOtp(payload: { orderNo: string; itemIds: number[] }) {
    return this.httpClient.post(`${this.apiUrl}orders/cancel-delivery/otp/skip`, payload);
  }

  generateDeliveryOtp(payload: { orderNo: string }) {
    return this.httpClient.post(`${this.apiUrl}orders/delivery/otp`, payload);
  }

  validateDeliveryOtp(payload: { orderNo: string; otp: string; deliveryMode: string }) {
    return this.httpClient.post(`${this.apiUrl}orders/delivery/otp/validate`, payload);
  }

  skipDeliveryOtp(payload: { orderNo: string }) {
    return this.httpClient.post(`${this.apiUrl}orders/delivery/otp/skip`, payload);
  }

  deliverByCourier(id: number, data: any, receiptAttachment?: File | null) {
    const formData = new FormData();
    formData.append(
      'data',
      new Blob([JSON.stringify(data)], { type: 'application/json' })
    );
    if (receiptAttachment) {
      formData.append('receiptAttachment', receiptAttachment);
    }
    return this.httpClient.post(`${this.apiUrl}orders/${id}/delivery/courier`, formData);
  }

  validateFileByOrderNo(orderNo: string) {
    return this.httpClient.get(`${this.apiUrl}orders/validate-file-by-order-no`, {
      params: { orderNo }
    });
  }

  validateFile(certificateVendorId: number, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post(`${this.apiUrl}orders/validate-file`, formData, {
      params: { certificateVendorId: String(certificateVendorId) }
    });
  }

  save(data: any, file?: File | null) {
    const formData = new FormData();
    formData.append(
      'data',
      new Blob([JSON.stringify(data)], { type: 'application/json' })
    );
    if (file) {
      formData.append('file', file);
    }
    return this.httpClient.post(`${this.apiUrl}orders`, formData);
  }

  replaceInputFile(id: number, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post(`${this.apiUrl}orders/${id}/input-file`, formData);
  }

  reviseUploadFile(id: number, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post(`${this.apiUrl}orders/${id}/revise-upload-file`, formData);
  }

  updateItemStatusBulk(payload: { status: string; itemIds: number[] }) {
    return this.httpClient.patch(`${this.apiUrl}orders/items/status`, payload);
  }

  updateStatus(
    id: number,
    status: string,
    comment?: string,
    extras?: { vendorId?: number; mobileLabFlag?: boolean }
  ) {
    const params: any = { status };
    if (comment) {
      params.comment = comment;
    }
    if (extras?.vendorId !== undefined && extras?.vendorId !== null) {
      params.vendorId = extras.vendorId;
    }
    if (extras?.mobileLabFlag !== undefined && extras?.mobileLabFlag !== null) {
      params.mobileLabFlag = extras.mobileLabFlag;
    }
    return this.httpClient.patch(`${this.apiUrl}orders/${id}/status`, null, { params });
  }

  unhold(id: number) {
    return this.httpClient.patch(`${this.apiUrl}orders/${id}/unhold`, null);
  }

  updateOrderIsMobileLab(id: number, value: boolean) {
    return this.httpClient.patch(`${this.apiUrl}orders/${id}/is-mobile-lab`, null, {
      params: { value: String(value) }
    });
  }

  assignOrderSrNo(id: number, startNo: string) {
    return this.httpClient.patch(`${this.apiUrl}orders/${id}/assign-sr-no`, null, {
      params: { startNo }
    });
  }

  delete(id: number) {
    return this.httpClient.delete(`${this.apiUrl}orders/${id}`);
  }

  updateOrderItemStatus(orderItemId: number, payload: { orderItemStatus: string; qcStatus?: string; boStatus?: string; vendorStatus?: string; comment?: string }) {
    return this.httpClient.post(`${this.apiUrl}orders/items/item-status`, payload, {
      params: { orderItemId: String(orderItemId) }
    });
  }

  updateOrderItemWorkflowStatusBulk(orderNo: string, payload: { orderItemStatus: string; qcStatus?: string; boStatus?: string; vendorStatus?: string; itemIds: number[]; comment?: string }) {
    return this.httpClient.post(`${this.apiUrl}orders/items/workflow-status`, payload, {
      params: { orderNo }
    });
  }

  updateOrderItemsCustomerComment(orderNo: string, payload: { itemIds: number[]; customerComment: string }) {
    return this.httpClient.post(`${this.apiUrl}orders/items/customer-comment`, payload, {
      params: { orderNo }
    });
  }

  updateOrderItemStateBulk(orderNo: string, payload: { orderState: string; itemIds: number[]; comment?: string }) {
    return this.httpClient.post(`${this.apiUrl}orders/items/order-state`, payload, {
      params: { orderNo }
    });
  }

  convertOrderItemsForInternalBilling(orderNo: string, payload: { itemIds: number[] }) {
    return this.httpClient.post(`${this.apiUrl}orders/items/internal-billing`, payload, {
      params: { orderNo }
    });
  }

  addToBin(orderId: number, payload: { dailyBinId: number; itemIds?: number[] }) {
    return this.httpClient.post(`${this.apiUrl}orders/${orderId}/add-to-bin`, payload);
  }

  statusUpdate(id: number, payload: { status: string; checklistIds: string; comment?: string }) {
    return this.httpClient.post(`${this.apiUrl}orders/${id}/status-update`, payload);
  }
}
